import { useState } from 'react';
import { DocumentArrowDownIcon } from '@heroicons/react/16/solid';
import { useTranslation } from 'react-i18next';
import {
  downloadAttachment,
  resolveAttachmentFilename,
} from 'src/utils/downloadAttachment';
import { tTypeAttachment } from '../../MessageBubble.type.ts';

/**
 * A chat-sourced URL rendered into an `<a href>` sink must never carry a
 * non-https or `javascript:` scheme (BUG-712; mirrors ImageContent).
 *
 * `blob:` passes too: an encrypted WhatsApp Web attachment arrives here with
 * its `url` already swapped for the object URL of the decrypted bytes
 * (`Chat.actions.decryptAttachment`), which is same-origin by construction.
 */
function safeHref(url: string | null): string | undefined {
  if (!url) return undefined;
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'blob:' ? url : undefined;
  } catch {
    return undefined;
  }
}

export default function FileContent(props: tTypeAttachment) {
  const { t } = useTranslation('chat/chat');
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  const href = safeHref(props.attachment.url);
  const filename = resolveAttachmentFilename(props.attachment);

  // BUG-1144: the click saves the file under the name its sender gave it, which
  // the browser cannot work out on its own — our storage serves the object
  // under a ULID key and without a Content-Disposition header. The element
  // stays an `<a href>` on purpose, so middle-click and "open in a new tab"
  // keep previewing the document the way they do today.
  async function handleDownload(url: string) {
    if (isDownloading) return;
    setIsDownloading(true);
    setDownloadFailed(false);
    try {
      await downloadAttachment(url, filename);
    } catch {
      setDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  }

  const body = (
    <>
      <DocumentArrowDownIcon className="w-6 h-6 text-primary" />
      <p className="text-xs text-ellipsis whitespace-nowrap overflow-auto max-w-80">
        {filename}
      </p>
    </>
  );

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="flex justify-center">
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-busy={isDownloading}
            onClick={(event) => {
              // A modifier-click is the browser's own "open in a new tab /
              // window / download" gesture. Intercepting it would take away the
              // preview this element keeps on purpose.
              if (
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey ||
                event.button !== 0
              ) {
                return;
              }
              event.preventDefault();
              void handleDownload(href);
            }}
            className="max-w-96 object-cover flex justify-center items-center flex-col"
          >
            {body}
          </a>
        ) : (
          <div className="max-w-96 object-cover flex justify-center items-center flex-col">
            {body}
          </div>
        )}
      </div>
      {downloadFailed && href && (
        // The click cancelled the browser's own navigation, so a failed fetch
        // would otherwise leave the document unreachable. This hands it back,
        // from a real user gesture no popup blocker will refuse.
        <div className="flex flex-col items-center gap-0.5">
          <p role="alert" className="text-xs text-[var(--ls-text-error)]">
            {t('downloadFileFailed')}
          </p>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-primary underline"
          >
            {t('downloadFileOpenInTab')}
          </a>
        </div>
      )}
    </div>
  );
}
